"use client";

import { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useRouter } from "@/i18n/navigation";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, getApiErrorCode } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/stores/auth-store";
import type { PublicPlan } from "@/lib/types/plan";
import { FadeIn } from "./fade-in";

export function Pricing() {
  const t = useTranslations("marketing.pricing");
  const tErrors = useTranslations("api-errors");
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user } = useAuthStore();
  const [annual, setAnnual] = useState(false);
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  // Whether Stripe has a subscription to modify. This is the only thing that
  // decides checkout vs change-plan, and it is NOT derivable from user.plan: a
  // trial account is created on Plan.Advanced with no subscription at all.
  // Routing on the plan sent every trial user to change-plan, where the backend
  // has nothing to change and returns 400, so "Switch to Starter" was broken
  // for every account in its first 14 days. null until the answer is known.
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);

  const isUpgradeMode = searchParams.get("upgrade") === "1";
  const userPlanLower = user?.plan?.toLowerCase() ?? null;

  // The badge used to read a hardcoded "Save 20%". At the current prices the
  // real saving is 14% to 16% depending on the tier, so the claim was simply
  // untrue. Computed from the live plans, and deliberately the SMALLEST saving
  // across them, so the number on the toggle holds for whichever plan the
  // visitor ends up choosing rather than only for the best case.
  const annualSavingPercent = useMemo(() => {
    const savings = plans
      .filter((p) => p.monthlyAmountUsd > 0 && p.annualAmountUsd > 0)
      .map((p) => 1 - p.annualAmountUsd / (p.monthlyAmountUsd * 12));
    if (savings.length === 0) return 0;
    return Math.floor(Math.min(...savings) * 100);
  }, [plans]);

  useEffect(() => {
    api.plans
      .list()
      .then((r) => setPlans(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Only signed-in visitors have billing status, and only upgrade mode acts on
  // it. Treat a failed lookup as "no subscription": that path is Checkout,
  // which works for a subscriber too (Stripe reuses the customer), whereas the
  // reverse guess dead-ends in a 400.
  useEffect(() => {
    if (!user || !isUpgradeMode) return;
    api.billing
      .status()
      .then((s) => setHasSubscription(!!s.subscriptionId))
      .catch(() => setHasSubscription(false));
  }, [user, isUpgradeMode]);

  const onCtaClick = async (planCode: string) => {
    if (!user) {
      router.push("/register");
      return;
    }
    setBusy(planCode);
    try {
      const interval = annual ? "annual" : "monthly";
      if (isUpgradeMode && hasSubscription) {
        const r = await api.billing.changePlan({ planCode, interval });
        if (r.immediate) {
          toast.success(t("planSwitched"));
          router.push("/settings/billing?ok=1");
        } else {
          const dateStr = r.scheduledFor
            ? new Date(r.scheduledFor).toLocaleDateString()
            : t("nextRenewal");
          toast.success(t("planChangeScheduled") + " " + dateStr);
          router.push("/settings/billing");
        }
      } else {
        const r = await api.billing.checkout({ planCode, interval });
        // eslint-disable-next-line react-hooks/immutability
        window.location.href = r.url;
      }
    } catch (e) {
      // ApiError sets .message to the error code, so the old
      // `toast.error(e.message)` printed the code itself. Translate it when the
      // code is one we have copy for, and fall back to generic copy otherwise
      // rather than showing the visitor a machine string.
      const failure = getApiErrorCode(e);
      toast.error(
        failure && tErrors.has(failure.errorCode)
          ? tErrors(failure.errorCode, failure.params as never)
          : t("genericError"),
      );
      setBusy(null);
    }
  };

  return (
    <section id="pricing" className="scroll-mt-20 px-6 py-24">
      <div className="mx-auto max-w-6xl">
        <FadeIn className="text-center">
          <p className="mb-4 text-[13px] font-bold uppercase tracking-[0.14em] text-primary">
            {t("kicker")}
          </p>
          <h2 className="text-balance text-3xl font-extrabold sm:text-4xl">
            {t("title")}
          </h2>
          <p className="mx-auto mt-3.5 max-w-2xl text-balance text-muted-foreground">
            {t("subtitle")}
          </p>

          {/* Monthly / Annual toggle: only meaningful when plan cards are shown */}
          {!loading && plans.length > 0 && (
            <div className="mt-8 flex items-center justify-center gap-3">
              <Label
                htmlFor="billing-toggle"
                className={cn(
                  "cursor-pointer text-sm",
                  !annual ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {t("monthly")}
              </Label>
              <Switch id="billing-toggle" checked={annual} onCheckedChange={setAnnual} />
              <Label
                htmlFor="billing-toggle"
                className={cn(
                  "cursor-pointer text-sm",
                  annual ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {t("annual")}{" "}
                <span className="text-primary">
                  {t("save", { percent: annualSavingPercent })}
                </span>
              </Label>
            </div>
          )}
        </FadeIn>

        {loading && (
          <p className="mt-12 text-center text-sm text-muted-foreground">
            {t("loading")}
          </p>
        )}

        {/* Launch state: paid plans not yet published, trial signup only */}
        {!loading && plans.length === 0 && (
          <FadeIn className="mx-auto mt-12 max-w-lg">
            <div
              data-testid="pricing-trial-card"
              className="flex flex-col items-center rounded-3xl border border-primary/45 bg-linear-to-b from-primary/10 to-secondary/10 p-8 text-center backdrop-blur-md"
            >
              <h3 className="text-balance text-lg font-bold">{t("emptyTitle")}</h3>
              <p className="mt-2.5 text-balance text-sm text-muted-foreground">
                {t("emptyBody")}
              </p>
              <Button
                onClick={() => router.push("/register")}
                variant="gradient"
                size="lg"
                className="mt-6 w-full sm:w-auto sm:px-10"
              >
                {t("startFreeTrial")}
              </Button>
            </div>
          </FadeIn>
        )}

        {!loading && plans.length > 0 && (
          <div className="mt-12 grid items-stretch gap-5 lg:grid-cols-3">
            {plans.map((plan, i) => {
              const isPopular = plan.planCode === "advanced";
              // annualAmountUsd is the YEARLY total, which is what the admin
              // editor collects and what Stripe stores (a $299 price with
              // interval=year). Both slots here are per-month, so it has to be
              // divided. Treating it as a monthly figure rendered "$299.00/mo"
              // on the annual toggle and "billed as $3,588.00/yr" underneath.
              const annualPerMonth = plan.annualAmountUsd / 12;
              const price = annual ? annualPerMonth : plan.monthlyAmountUsd;

              return (
                <FadeIn
                  key={plan.planCode}
                  delay={i * 0.1}
                  data-testid="plan-card"
                  data-popular={isPopular || undefined}
                  className={cn(
                    "relative flex flex-col rounded-3xl border bg-card p-7 backdrop-blur-md",
                    // Popular plan reorders first on mobile (single column),
                    // back to natural position on lg (3 columns).
                    isPopular &&
                      "order-first border-primary/45 bg-linear-to-b from-primary/10 to-secondary/10 shadow-[0_20px_60px_rgb(var(--primary-glow)/0.12)] lg:order-none",
                  )}
                >
                  {isPopular && (
                    <span className="absolute -top-3 start-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-linear-to-r from-primary to-indigo-500 px-3.5 py-1 text-[11px] font-bold tracking-wide text-primary-foreground rtl:translate-x-1/2">
                      {t("mostPopular")}
                    </span>
                  )}

                  <h3 className="text-lg font-bold">{plan.displayName}</h3>

                  <div className="mt-2.5 flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-extrabold">
                      ${price.toFixed(2)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {t("perMonth")}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {annual
                      ? t("billedAnnuallyTotal", {
                          price: `$${plan.annualAmountUsd.toFixed(2)}`,
                        })
                      : t("orAnnually", {
                          price: `$${annualPerMonth.toFixed(2)}`,
                        })}
                  </p>

                  <ul className="mb-7 mt-6 flex flex-col gap-2.5 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-primary" />
                      {plan.limits.signalsInMonth === -1
                        ? t("unlimitedSignalsPerMonth")
                        : t("signalsPerMonth", {
                            count: plan.limits.signalsInMonth.toLocaleString(),
                          })}
                    </li>
                    <li className="flex items-start gap-2">
                      <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-primary" />
                      {t("sourcesAndConnections", {
                        sources: plan.limits.maxSources,
                        connections: plan.limits.maxConnections,
                      })}
                    </li>
                    {plan.features.prioritySupport && (
                      <li className="flex items-start gap-2">
                        <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-primary" />
                        {t("prioritySupport")}
                      </li>
                    )}
                  </ul>

                  {/* "Current plan" means Stripe is billing them for it, not
                      that the trial happens to run on it. A trial account sits
                      on Advanced, so keying this off user.plan alone replaced
                      the Advanced button with a dead badge, leaving the plan
                      they were already trying no way to be bought: the single
                      most likely conversion on the page. */}
                  {isUpgradeMode && hasSubscription && plan.planCode === userPlanLower ? (
                    <div className="mt-auto rounded-lg border border-primary/40 bg-primary/15 py-2.5 text-center text-sm font-semibold text-primary">
                      {t("currentPlan")}
                    </div>
                  ) : (
                    <Button
                      onClick={() => onCtaClick(plan.planCode)}
                      disabled={busy !== null}
                      variant={isPopular ? "gradient" : "glass"}
                      size="lg"
                      className="mt-auto w-full"
                    >
                      {busy === plan.planCode
                        ? "..."
                        : !isUpgradeMode
                          ? t("startFreeTrial")
                          : hasSubscription
                            ? t("switchTo") + " " + plan.displayName
                            : t("choosePlan", { plan: plan.displayName })}
                    </Button>
                  )}
                </FadeIn>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
